import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
}

interface TabsProps<T extends string> {
  tabs: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  /** "pill" = filled container (default), "underline" = bottom border style. */
  variant?: "pill" | "underline";
  /** Stretch tabs to fill the container width (pill variant only). */
  fullWidth?: boolean;
}

export function Tabs<T extends string>({ tabs, value, onChange, className, variant = "pill", fullWidth }: TabsProps<T>) {
  if (variant === "underline") {
    return (
      <div role="tablist" className={cn("flex max-w-full flex-wrap gap-1 border-b border-studio-border", className)}>
        {tabs.map((t) => {
          const active = value === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(t.value)}
              className={cn(
                "-mb-px flex min-h-11 min-w-0 items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors",
                active
                  ? "border-studio-accent text-studio-ink"
                  : "border-transparent text-studio-muted hover:text-studio-ink"
              )}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex max-w-full flex-wrap rounded-xl border border-studio-border bg-studio-card p-1",
        fullWidth && "flex w-full",
        className
      )}
    >
      {tabs.map((t) => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.value)}
            className={cn(
              "flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-lg py-1.5 text-sm font-medium transition-[background-color,color] sm:min-h-8",
              fullWidth ? "flex-1 px-2 sm:px-4" : "px-4",
              active ? "bg-studio-accent text-studio-accent-fg" : "text-studio-muted hover:text-studio-ink"
            )}
          >
            {t.icon}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
